// src/components/property/AIReportGenerator.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import './AIReportGenerator.css';
import { UserContext } from '../../context/UserContext';
import { NotificationContext } from '../../context/NotificationContext';
import { BASE_URL } from '../../utils/config';
import { apiFetch } from '../../utils/apiClient'; // Using your apiFetch utility

// This component now assumes a purchase has already been made.
const AIReportGenerator = ({ propertyId, orderId, fullAddress, onReportInitiated }) => {
  // The initial message is now more generic, as initiation is handled by the backend.
  const { t } = useTranslation();
  const [statusMessage, setStatusMessage] = useState(t('aiReportGenerator.initialStatus'));
  const [statusType, setStatusType] = useState('info'); // 'info', 'success', 'error'
  const [dotCount, setDotCount] = useState(0); // New state for animating dots
  const { user, refreshUser, updateUser } = useContext(UserContext);
  const { addNotification } = useContext(NotificationContext);
  const lang = useLanguage();
  const pollingIntervalRef = useRef(null);

  // This effect now only refreshes the user's context (for credit balance)
  // and notifies the parent component. It no longer triggers the report.
  useEffect(() => {
    console.log('[AIReportGenerator] Component mounted for polling, refreshing user context.');
    refreshUser();

    if (onReportInitiated) {
      onReportInitiated();
    }
    // We only want this to run once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // useEffect for polling the order status
  useEffect(() => {
    if (!orderId) return;

    const checkStatus = async () => {
      try {
        const url = `${BASE_URL}/api/orders/${orderId}/status`;
        const response = await apiFetch(url, {}, user.token);
        if (!response.ok) {
          console.warn(`Polling failed for order ${orderId}, status ${response.status}`);
          // We don't animate dots if the backend is unresponsive.
          return;
        }
        const { order, user: updatedUser } = await response.json();
        const terminalStates = ['completed', 'failed', 'generation-failed', 'action-required'];

        if (terminalStates.includes(order.status)) {
          // If the backend sends updated user data (e.g., on order completion), update the context.
          if (updatedUser) {
            updateUser({ creditPoints: updatedUser.creditPoints, pointsOnHold: updatedUser.pointsOnHold });
          }

          clearInterval(pollingIntervalRef.current); // Stop polling
          if (order.status === 'completed') {
            const address = order.propertyDetails?.address || fullAddress;
            const successMsg = t('aiReportGenerator.reportSent', { address, email: user.email });
            const successMsgWithLink = t('aiReportGenerator.reportSentWithLink', { address, email: user.email, myOrdersPage: `/${lang}/my-orders` });

            setStatusMessage(successMsg);
            setStatusType('success');
            addNotification(successMsgWithLink, 'success', 10000); // Play sound on success
          } else {
            setStatusMessage(t('aiReportGenerator.generationFailed', { status: order.status }));
            setStatusType('error');
            addNotification(t('aiReportGenerator.generationFailedNotification', { orderId }), 'error');
          }
        } else {
          // If polling is successful and not in a terminal state, update status and animate dots.
          if (order.status === 'generating-report') {
            setStatusMessage(t('aiReportGenerator.generatingReport', { email: user.email }));
          } else if (order.status === 'generating-pdf') {
            setStatusMessage(t('aiReportGenerator.generatingPdf'));
          }
          // Animate the dots
          setDotCount((prevCount) => (prevCount + 1) % 6);
        }
      } catch (err) {
        console.error('Error during status polling:', err);
        // We also don't animate dots if the poll request itself fails.
      }
    };

    checkStatus(); // Initial check
    pollingIntervalRef.current = setInterval(checkStatus, 5000); // Poll every 5 seconds

    return () => clearInterval(pollingIntervalRef.current); // Cleanup on unmount
  }, [orderId, user.token, addNotification, fullAddress, user.email, updateUser, lang, t]);

  return (
    <div className="ai-report-generator">
      <h3>{t('aiReportGenerator.title')}</h3>
      <p>
        <strong>{fullAddress}</strong>
      </p>
      <div className="report-view-content section-spacing">
        <div className={`status-message status-message-${statusType}`}>
          {statusMessage}
          {/* Only show animating dots when polling is active */}
          {statusType === 'info' && <span className="dots">{'.'.repeat(dotCount)}</span>}
          {statusType === 'success' && (
            <p>
              <Trans i18nKey="aiReportGenerator.viewOrdersLink">
                You can view all your orders and download recent reports from the <Link to={`/${lang}/my-orders`}>My Orders</Link> page.
              </Trans>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AIReportGenerator;
